import { UUID } from "@bitory-gmbh/nidavellir";
import { backendApi } from "@/services/backend/api";
import { base64ListRequest } from "@/services/backend/_lib/base64-list-request";
import { DefaultBaseData } from "@/services/backend/shared/base-data-kit/types/default-base-data";
import { Filter } from "@/shared/lib/filter/filter";
import { Lst } from "@/shared/lib/lst/lst";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";

/**
 * Client for the `basedatakit.Default` endpoints. Every subdomain mounts
 * the same routes under its own prefix, so the subdomain is part of every
 * request instead of the api being copied per subdomain.
 *
 * The tag is flat: a Default has no identity of its own, and the lists are
 * small enough that invalidating all of them costs nothing.
 */
const defaultBaseDataApi = backendApi
  .enhanceEndpoints({
    addTagTypes: ["DefaultBaseData"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      /**
       * Lists the defaults of the given companies and keys.
       *
       * The list — not the show endpoint — is what the UI reads: showing a
       * default that is not set answers 404, which would make every row of
       * a base data table render an error. An empty list says the same
       * thing without one.
       */
      defaultBaseDataList: build.query<Lst<DefaultBaseData>, ListRequest>({
        query: ({ subdomain, ...request }) => ({
          url: `/${subdomain}/default-base-data`,
          method: "GET",
          params: base64ListRequest(request),
        }),
        providesTags: ["DefaultBaseData"],
      }),
      defaultBaseDataPut: build.mutation<DefaultBaseData, PutRequest>({
        query: ({ subdomain, ...request }) => ({
          url: `/${subdomain}/default-base-data`,
          method: "PUT",
          body: request,
        }),
        invalidatesTags: ["DefaultBaseData"],
      }),
      defaultBaseDataDelete: build.mutation<null, DeleteRequest>({
        query: ({ subdomain, key, companyId }) => ({
          url: `/${subdomain}/default-base-data/${key}/companies/${companyId}`,
          method: "DELETE",
        }),
        invalidatesTags: ["DefaultBaseData"],
      }),
    }),
  });

export const {
  useDefaultBaseDataListQuery,
  useDefaultBaseDataPutMutation,
  useDefaultBaseDataDeleteMutation,
} = defaultBaseDataApi;

/** The backend subdomain the defaults are mounted under, e.g. "htz". */
export type BaseDataSubdomain = string;

/** The columns the backend can order the defaults by. */
export type DefaultBaseDataOrderField = "key" | "companyId" | "id";

export interface ListRequest {
  subdomain: BaseDataSubdomain;
  /**
   * Required by the backend: it must be active and hold at least one
   * company, otherwise the request fails its own validation.
   */
  company: Filter<UUID>;
  key?: Filter<string>;
  id?: Filter<UUID>;
  orderBy?: OrdrBy<DefaultBaseDataOrderField>[];
  limit?: number;
  offset?: number;
}

export interface PutRequest {
  subdomain: BaseDataSubdomain;
  companyId: UUID;
  key: string;
  /** The base data instance to be used as default. */
  id: UUID;
}

export interface DeleteRequest {
  subdomain: BaseDataSubdomain;
  companyId: UUID;
  key: string;
}
